import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {AnalyticsEventActions} from '@mintdata-wsp/common/controllers/analytics/AnalyticsEventActions';
import {ModifyOrganizationRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ModifyOrganizationRequest';
import {Organization} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {Form, FormProps} from '@mintdata-wsp/wsp/ui/components/forms/data/Form';
import {ErrorPopupSide, Input, InputSize, InputStyle} from '@mintdata-wsp/wsp/ui/components/forms/Input';
import {InputHelp} from '@mintdata-wsp/wsp/ui/components/forms/InputHelp';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {ButtonSize, ButtonStyle, WSPButton} from '@mintdata-wsp/wsp/ui/components/general/WSPButton';
import {SettingsHeader} from '@mintdata-wsp/wsp/ui/views/workspace/settings/SettingsHeader';
import {SettingsRoutes} from '@mintdata-wsp/wsp/ui/views/workspace/settings/SettingsPanel';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {RouteComponentProps} from 'react-router';

const styles = require('./OrgGeneralView.less');
const cx = classnames.bind(styles);

export class OrgGeneralViewState
{
    isSaveButtonDisplayed:boolean = false;
    isSaveButtonDisabled:boolean = true;
    organization:Organization;
}

@observer
export class OrgGeneralView extends React.Component<RouteComponentProps<any>, OrgGeneralViewState>
{
    constructor()
    {
        super();
        this.state = new OrgGeneralViewState();
    }

    private _form:Form = new Form({onChange: this._onFormChanged} as FormProps);

    @bind
    private _onFormChanged(fieldKey:string, fieldValue:any, isValid:boolean):void
    {
        this._checkUiState();
    }

    @action.bound
    private _checkUiState():void
    {
        const areSomeFieldChanged = this._form.areSomeFieldChanged();
        const areAllFieldsValid = this._form.areAllFieldsValid();
        const isSaveButtonDisabled = !(areSomeFieldChanged && areAllFieldsValid);

        this.setState({isSaveButtonDisabled});
    }

    @action.bound
    private _onSetEditMode():void
    {
        this.setState({isSaveButtonDisplayed: true});
    }

    @bind
    _onSave()
    {
        if( !this._form.areSomeFieldChanged() || !this._form.areAllFieldsValid() )
            return;

        let {currentOrganization} = rootState.userSession;

        const newOrgName = this._form.get('name');
        const orgSid = currentOrganization.siteId;

        controllers.data.organizations.update(orgSid, new ModifyOrganizationRequest(newOrgName, orgSid))
            .then((organization) =>
            {
                const userSession = {...rootState.userSession};
                userSession.currentOrganization = organization;
                controllers.user.saveToRootDomainCookie(userSession);

                controllers.data.organizations.syncAllOrganizations();
                this._initState(organization);
                this._resetForm();
            })
            .catch((err) =>
            {
                console.warn(err);
            });
    }

    componentWillMount():void
    {
        this._initState(rootState.userSession.currentOrganization);
    }

    componentDidMount():void
    {
        controllers.analytics.main.trackEvent(AnalyticsEventActions.WSP_PAGE_LOADED);
    }

    render()
    {
        const {isSaveButtonDisabled, isSaveButtonDisplayed, organization} = this.state;

        return (
            <Column className={cx('orgGeneralView')}>

                <SettingsHeader
                    text={'General Settings'}
                    selectedRoute={SettingsRoutes.General}
                />

                <Column className={cx('general')}>

                    <Input
                        className={cx('inputName')}
                        icon={MDIcons.common.OrganizationHouse}
                        initialValue={organization.name}
                        size={InputSize.ExtraLarge}
                        style={InputStyle.Dialog}
                        isClickForEdit={true}
                        formKey={'name'}
                        form={this._form}
                        onSetEditMode={this._onSetEditMode}
                        onEnterKey={this._onSave}
                        errorPopupSide={ErrorPopupSide.Bottom}
                        isValidFn={(value:string) =>
                        {
                            return value && value.trim().length > 4 && value.trim().length <= 20;
                        }}
                        getErrorMessageFn={(value:string) =>
                        {
                            if( value.length < 5 && value.length > 1 )
                            {
                                return 'Organization name should be at least 5 symbols long.';
                            }
                            if( value.length > 20 )
                            {
                                return 'Organization name should be shorter than 20 symbols.';
                            }

                            return 'Please enter a name for your organization.';
                        }}
                    />

                    <InputHelp
                        className={cx('inputHelp')}
                        label={'Use organization name to differentiate your spaces for other people.'}
                    />

                    <Input
                        className={cx('inputDomain')}
                        isReadOnly={true}
                        icon={MDIcons.common.World}
                        size={InputSize.ExtraLarge}
                        style={InputStyle.Dialog}
                        initialValue={organization.siteId + '.mintdata.com'}
                    />

                    <InputHelp
                        className={cx('inputHelp')}
                    >
                        To change organization site <a
                        href={`mailto:support@mintdata.com?subject=SiteID Change: ${organization.siteId}.mintdata.com&body=Hello,%0D%0DPlease change the current URL for our organization:%0D%0Dhttps://${organization.siteId}.mintdata.com%0D%0Dto the new URL of:%0D%0Dhttps://_________.mintdata.com%0D%0DThank you.`}
                    >
                        contact support
                    </a>
                    </InputHelp>

                    <Row className={cx('buttons')}>
                        {isSaveButtonDisplayed && (
                            <WSPButton
                                className={cx('buttonSave')}
                                caption={'Save'}
                                size={ButtonSize.Large}
                                style={ButtonStyle.Raised}
                                isDisabled={isSaveButtonDisabled}
                                onClick={this._onSave}
                            />
                        )}
                    </Row>

                </Column>

            </Column>
        );
    }

    private _initState(organization:Organization)
    {
        this.setState({organization, isSaveButtonDisabled: true, isSaveButtonDisplayed: true});
    }

    private _resetForm()
    {
        this._form.setInitValue('name', this.state.organization.name);
    }
}
